/**
 * Setup Fees Manager v2.0
 * - Hides setup fee products from collection/search pages
 * - Shows fee preview notices directly below the file setup help text
 * - Auto-adds/removes/syncs setup fee line items in cart
 * - Protects fee items in cart (no remove button, locked quantity)
 * - Blocks checkout when cart minimum not met
 */

(function () {
  'use strict';

  // ============================================================
  // CONFIG
  // ============================================================

  const CONFIG = {
    silkScreen: {
      variantId: 45787698495676,
      price: 15,
      name: 'Silk Screen Setup Fee',
    },
    embroidery: {
      variantId: 45791285477564,
      price: 30,
      name: 'Embroidery Setup Fee',
    },
  };

  const PROTECTED_IDS = [CONFIG.silkScreen.variantId, CONFIG.embroidery.variantId];

  // ============================================================
  // CART API HELPERS
  // ============================================================

  async function getCart() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (e) {
      console.error('❌ getCart failed:', e);
      return { items: [] };
    }
  }

  async function cartAdd(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!res.ok) throw new Error(`cart/add failed: ${res.status}`);
    return res.json();
  }

  async function cartChange(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error(`cart/change failed: ${res.status}`);
    return res.json();
  }

  // ============================================================
  // COLLECTION PAGE — hide setup fee products
  // ============================================================

  function hideSetupFeeProducts() {
    if (!window.location.pathname.includes('/collections') && !window.location.pathname.includes('/search')) return;
    document.querySelectorAll('#product-grid > li.grid__item').forEach(item => {
      if (item.querySelector('a[href*="setup-fee"]')) item.remove();
    });
  }

  // ============================================================
  // PRODUCT PAGE — read selected options
  // ============================================================

  function getSelectedOptions() {
    const options = {};
    document.querySelectorAll('.ymq-options-box:not(.ymq-option-condition-hide)').forEach(box => {
      const label = box.querySelector('.ymq_lable')?.textContent?.trim().toLowerCase();
      if (!label) return;

      const checked = box.querySelector('input:checked');
      if (checked?.value) { options[label] = checked.value.toLowerCase(); return; }

      const dropdown = box.querySelector('.ymq-app-select-values');
      if (dropdown) {
        const text = dropdown.textContent.trim();
        if (text && !text.includes('Please choose') && text !== '--') {
          options[label] = text.toLowerCase();
        }
      }
    });
    return options;
  }

  function getLogoCount(options) {
    const p = options['logo placement'] || '';
    if (p.includes('3 logo') || p.startsWith('3')) return 3;
    if (p.includes('2 logo') || p.startsWith('2')) return 2;
    if (p.includes('1 logo') || p.startsWith('1') || p.includes('left chest')) return 1;
    return 0;
  }

  function getScreenCount(options) {
    const ink = options['ink colors'] || '';
    const sides = (options['print sides'] || '').toLowerCase().replace(/[–—]/g, '-');

    let colors = 0;
    if (ink.includes('3')) colors = 3;
    else if (ink.includes('2')) colors = 2;
    else if (ink.includes('1')) colors = 1;

    let sideCount = 0;
    if (sides.includes('two') || sides.includes('2') || (sides.includes('front') && sides.includes('back'))) {
      sideCount = 2;
    } else if (sides.length > 0) {
      sideCount = 1;
    }

    return colors * sideCount;
  }

  // ============================================================
  // PRODUCT PAGE — fee notice DOM insertion
  //
  // Target: right after the "Reordering with us?" help text that
  // follows the Embroidery File Setup / Screen Setup option box.
  // ============================================================

  function getFeeInsertionPoint() {
    // Walk all visible option boxes to find the file/screen setup one
    const boxes = document.querySelectorAll('.ymq-options-box:not(.ymq-option-condition-hide)');
    let targetBox = null;

    boxes.forEach(box => {
      const label = box.querySelector('.ymq_lable')?.textContent?.trim().toLowerCase() || '';
      if (label.includes('embroidery file setup') || label.includes('screen setup')) {
        targetBox = box;
      }
    });

    if (targetBox) {
      // Look for a help-text sibling immediately after (the "Reordering with us?" line)
      let sibling = targetBox.nextElementSibling;
      while (sibling) {
        const text = sibling.textContent?.toLowerCase() || '';
        const isHelpText = text.includes('reorder') || text.includes('digitized') || text.includes('dst');
        const isNewSection = sibling.classList.contains('ymq-options-box') || sibling.id?.includes('upload');

        if (isHelpText) return { ref: sibling, position: 'after' };
        if (isNewSection) break;
        sibling = sibling.nextElementSibling;
      }
      // No help text found — insert directly after the option box
      return { ref: targetBox, position: 'after' };
    }

    // Fallback: before the upload section
    const upload = document.getElementById('tbd-upload-section');
    if (upload) return { ref: upload, position: 'before' };

    // Fallback: before quantity
    const qty = document.querySelector('.product-form__quantity');
    if (qty) return { ref: qty, position: 'before' };

    return null;
  }

  function insertElement(el, point) {
    if (!point) return;
    if (point.position === 'after') {
      point.ref.parentNode.insertBefore(el, point.ref.nextSibling);
    } else {
      point.ref.parentNode.insertBefore(el, point.ref);
    }
  }

  function removeFeeNotice(id) {
    document.getElementById(id)?.remove();
  }

  function showFeeNotice(id, { title, detail, note }) {
    removeFeeNotice(id);
    const point = getFeeInsertionPoint();
    if (!point) return;

    const el = document.createElement('div');
    el.id = id;
    el.style.cssText = 'margin: 0;';
    el.innerHTML = `
      <div style="
        background:#f1f8f4;
        border-left:3px solid #4caf50;
        padding:8px 12px;
        border-radius:4px;
        font-size:12px;
        color:#2e7d32;
        line-height:1.5;
      ">
        <strong style="font-size:13px;">✓ ${title}:</strong>
        <span style="opacity:0.9;">${detail}</span>
        ${note ? `<em style="opacity:0.7;font-size:11px;margin-left:6px;">${note}</em>` : ''}
      </div>
    `;
    insertElement(el, point);
  }

  // ============================================================
  // PRODUCT PAGE — decide what notice to show
  // ============================================================

  function updateFeeNotices() {
    const options = getSelectedOptions();
    const decoration = options['decoration method'] || '';
    const fileSetup = options['embroidery file setup'] || '';
    const screenSetup = options['screen setup'] || '';

    removeFeeNotice('screen-fee-notice');
    removeFeeNotice('emb-fee-notice');

    if (decoration.includes('silk screen')) {
      if (screenSetup.includes('reorder') || screenSetup.includes('same artwork')) {
        showFeeNotice('screen-fee-notice', {
          title: 'Reorder – No Setup Fee',
          detail: 'Using existing screens from previous order',
        });
      } else {
        const screens = getScreenCount(options);
        if (screens > 0) {
          showFeeNotice('screen-fee-notice', {
            title: 'Setup Fee',
            detail: `${screens} screen${screens > 1 ? 's' : ''} × $${CONFIG.silkScreen.price} = $${screens * CONFIG.silkScreen.price}`,
            note: 'One-time per order',
          });
        }
      }
      return;
    }

    if (decoration.includes('embroidery')) {
      const needsDigitization = fileSetup.includes('digitize') || fileSetup.includes('+$30');
      const hasOwnFile = fileSetup.includes('already have') || fileSetup.includes('dst');
      const isReorder = fileSetup.includes('reorder');

      if (needsDigitization) {
        const logos = getLogoCount(options);
        if (logos > 0) {
          showFeeNotice('emb-fee-notice', {
            title: 'Digitization Fee',
            detail: `${logos} location${logos > 1 ? 's' : ''} × $${CONFIG.embroidery.price} = $${logos * CONFIG.embroidery.price}`,
            note: 'One-time per order',
          });
        }
      } else if (isReorder) {
        showFeeNotice('emb-fee-notice', {
          title: 'Reorder – No Setup Fee',
          detail: 'Using your existing digitized file',
        });
      } else if (hasOwnFile) {
        showFeeNotice('emb-fee-notice', {
          title: 'No Setup Fee',
          detail: 'You have your own DST file ready',
        });
      }
    }
  }

  // ============================================================
  // CART — calculate what fees should be present
  // ============================================================

  function itemNeedsDigitization(item) {
    const decoration = (item.properties?.['Decoration Method'] || '').toLowerCase();
    const fileSetup = (item.properties?.['Embroidery File Setup'] || '').toLowerCase();
    return (
      decoration.includes('embroidery') &&
      (fileSetup.includes('digitize') || fileSetup.includes('+$30')) &&
      !fileSetup.includes('already have') &&
      !fileSetup.includes('dst') &&
      !fileSetup.includes('reorder')
    );
  }

  function itemNeedsScreenSetup(item) {
    const decoration = (item.properties?.['Decoration Method'] || '').toLowerCase();
    const screenSetup = (item.properties?.['Screen Setup'] || '').toLowerCase();
    return (
      decoration.includes('silk screen') &&
      !screenSetup.includes('reorder') &&
      !screenSetup.includes('same artwork')
    );
  }

  function getLogoCountFromItem(item) {
    const p = (item.properties?.['Logo Placement'] || '').toLowerCase();
    if (p.includes('3 logo') || p.startsWith('3')) return 3;
    if (p.includes('2 logo') || p.startsWith('2')) return 2;
    if (p.includes('1 logo') || p.startsWith('1') || p.includes('left chest')) return 1;
    return 0;
  }

  function getScreenCountFromItem(item) {
    const ink = item.properties?.['Ink Colors'] || '';
    const sides = (item.properties?.['Print Sides'] || '').toLowerCase().replace(/[–—]/g, '-');

    let colors = 0;
    if (ink.includes('3')) colors = 3;
    else if (ink.includes('2')) colors = 2;
    else if (ink.includes('1')) colors = 1;

    let sideCount = 0;
    if (sides.includes('two') || sides.includes('2') || (sides.includes('front') && sides.includes('back'))) {
      sideCount = 2;
    } else if (sides.length > 0) {
      sideCount = 1;
    }

    return colors * sideCount;
  }

  async function getRequiredFees() {
    const cart = await getCart();
    let maxLogos = 0, maxScreens = 0;
    let needsEmb = false, needsScreen = false;

    cart.items.forEach(item => {
      if (PROTECTED_IDS.includes(item.variant_id)) return;

      if (itemNeedsDigitization(item)) {
        needsEmb = true;
        maxLogos = Math.max(maxLogos, getLogoCountFromItem(item));
      }
      if (itemNeedsScreenSetup(item)) {
        needsScreen = true;
        maxScreens = Math.max(maxScreens, getScreenCountFromItem(item));
      }
    });

    return {
      embroidery: { needed: needsEmb && maxLogos > 0, quantity: maxLogos },
      silkScreen: { needed: needsScreen && maxScreens > 0, quantity: maxScreens },
    };
  }

  // ============================================================
  // CART — sync fee line items
  // ============================================================

  let syncing = false;

  async function syncFees() {
    if (syncing) return;
    syncing = true;

    try {
      const [fees, cart] = await Promise.all([getRequiredFees(), getCart()]);

      const embItem = cart.items.find(i => i.variant_id === CONFIG.embroidery.variantId);
      const screenItem = cart.items.find(i => i.variant_id === CONFIG.silkScreen.variantId);

      let changed = false;

      // Embroidery fee
      if (fees.embroidery.needed) {
        if (!embItem) {
          await cartAdd(CONFIG.embroidery.variantId, fees.embroidery.quantity);
          changed = true;
        } else if (embItem.quantity !== fees.embroidery.quantity) {
          await cartChange(embItem.key, fees.embroidery.quantity);
          changed = true;
        }
      } else if (embItem) {
        await cartChange(embItem.key, 0);
        changed = true;
      }

      // Silk screen fee
      if (fees.silkScreen.needed) {
        if (!screenItem) {
          await cartAdd(CONFIG.silkScreen.variantId, fees.silkScreen.quantity);
          changed = true;
        } else if (screenItem.quantity !== fees.silkScreen.quantity) {
          await cartChange(screenItem.key, fees.silkScreen.quantity);
          changed = true;
        }
      } else if (screenItem) {
        await cartChange(screenItem.key, 0);
        changed = true;
      }

      if (changed && window.location.pathname.includes('/cart')) {
        setTimeout(() => window.location.reload(), 300);
      }
    } catch (e) {
      console.error('❌ syncFees error:', e);
    }

    syncing = false;
  }

  // ============================================================
  // CART — protect fee line items (lock qty, hide remove btn)
  // ============================================================

  function lockFeeRows() {
    document.querySelectorAll('.cart-item, tr[class*="cart-item"]').forEach(row => {
      if (!row.textContent?.includes('Setup Fee')) return;
      if (row.dataset.feeLocked) return;
      row.dataset.feeLocked = 'true';

      // Lock quantity input
      row.querySelectorAll('input.quantity__input, .quantity__input').forEach(input => {
        input.disabled = true;
        input.readOnly = true;
        input.style.cssText += 'opacity:0.5;pointer-events:none;background:#f5f5f5;';
      });

      // Disable +/- buttons
      row.querySelectorAll('button.quantity__button').forEach(btn => {
        btn.disabled = true;
        btn.style.cssText += 'opacity:0.3;pointer-events:none;';
      });

      // Hide remove button / link
      row.querySelectorAll(
        'cart-remove-button, button.cart-remove-button, a[aria-label*="Remove"], a[href*="/cart/change"]'
      ).forEach(el => {
        el.style.cssText += 'display:none!important;visibility:hidden;pointer-events:none;';
        el.setAttribute('hidden', 'true');
      });

      // Add explanatory note
      const details = row.querySelector('.cart-item__details, td.cart-item__details');
      if (details && !details.querySelector('.fee-note')) {
        const note = document.createElement('div');
        note.className = 'fee-note';
        note.style.cssText = 'color:#999;font-size:11px;font-style:italic;margin-top:4px;';
        note.textContent = 'Required setup fee · managed automatically';
        details.appendChild(note);
      }
    });
  }

  // ============================================================
  // CART — item count next to "Estimated total"
  // ============================================================

  async function showItemCount() {
    if (!window.location.pathname.includes('/cart')) return;
    document.getElementById('cart-item-count')?.remove();

    const cart = await getCart();
    const total = cart.items
      .filter(i => !PROTECTED_IDS.includes(i.variant_id))
      .reduce((sum, i) => sum + i.quantity, 0);

    document.querySelectorAll('p, span, div, h2, h3, h4').forEach(el => {
      if (el.textContent.trim() === 'Estimated total' && !el.querySelector('#cart-item-count')) {
        const span = document.createElement('span');
        span.id = 'cart-item-count';
        span.style.cssText = 'font-weight:normal;color:#666;margin-left:5px;';
        span.textContent = `(${total} item${total !== 1 ? 's' : ''})`;
        el.appendChild(span);
      }
    });
  }

  // ============================================================
  // CART — block checkout when minimum not met
  // ============================================================

  function updateCheckoutBlocking() {
    const blocked = window.cartMinimumNotMet === true;
    const sel = [
      '#CartDrawer-Checkout',
      '.cart__checkout-button',
      '.cart__ctas button[name="checkout"]',
      'cart-drawer button[name="checkout"]',
      '.drawer__footer button[name="checkout"]',
      '.cart-drawer__footer button[name="checkout"]',
      'form[action="/cart"] button[name="checkout"]',
      '.cart__footer button[name="checkout"]',
      'a[href="/checkout"]',
    ].join(',');

    document.querySelectorAll(sel).forEach(btn => {
      if (blocked) {
        btn.style.setProperty('display', 'none', 'important');
        btn.style.setProperty('pointer-events', 'none', 'important');
      } else {
        btn.style.removeProperty('display');
        btn.style.removeProperty('pointer-events');
      }
    });

    document.body.classList.toggle('cart-minimum-not-met', blocked);
  }

  // ============================================================
  // CART — poll for external changes (quantity updates, etc.)
  // ============================================================

  function cartSignature(cart) {
    return cart.items
      .filter(i => !PROTECTED_IDS.includes(i.variant_id))
      .map(i => {
        const p = i.properties || {};
        return [
          i.variant_id,
          i.quantity,
          p['Decoration Method'] || '',
          p['Logo Placement'] || '',
          p['Embroidery File Setup'] || '',
          p['Ink Colors'] || '',
          p['Print Sides'] || '',
          p['Screen Setup'] || '',
        ].join(':');
      })
      .sort()
      .join('|');
  }

  let lastSig = null;

  async function pollCart() {
    const cart = await getCart();
    const sig = cartSignature(cart);
    if (lastSig !== null && lastSig !== sig) {
      lastSig = sig;
      await syncFees();
    } else {
      lastSig = sig;
    }
  }

  // ============================================================
  // IMMEDIATE PROTECTION CSS (runs before DOM is ready)
  // ============================================================

  function injectProtectionCSS() {
    if (document.getElementById('sfm-protection-css')) return;
    const style = document.createElement('style');
    style.id = 'sfm-protection-css';
    style.textContent = `
      /* Hide remove buttons on fee items via CSS as backup */
      .cart-item[data-fee-locked] cart-remove-button,
      .cart-item[data-fee-locked] a[aria-label*="Remove"],
      .cart-item[data-fee-locked] button.cart-remove-button,
      tr[data-fee-locked] cart-remove-button,
      tr[data-fee-locked] a[aria-label*="Remove"],
      tr[data-fee-locked] button.cart-remove-button {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      /* Block checkout button when minimum not met */
      body.cart-minimum-not-met #CartDrawer-Checkout,
      body.cart-minimum-not-met .cart__checkout-button,
      body.cart-minimum-not-met .cart__ctas button[name="checkout"],
      body.cart-minimum-not-met cart-drawer button[name="checkout"],
      body.cart-minimum-not-met .drawer__footer button[name="checkout"],
      body.cart-minimum-not-met form[action="/cart"] button[name="checkout"],
      body.cart-minimum-not-met a[href="/checkout"] {
        display: none !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  // ============================================================
  // INTERCEPT fetch to catch cart/add calls
  // ============================================================

  function interceptFetch() {
    const orig = window.fetch;
    window.fetch = async function (...args) {
      const url = String(args[0] || '');
      const result = await orig.apply(this, args);
      if (url.includes('/cart/add')) {
        setTimeout(syncFees, 800);
      }
      return result;
    };
  }

  // ============================================================
  // INIT
  // ============================================================

  function initProductPage() {
    // Listen for option changes
    document.addEventListener('click', e => {
      if (e.target.closest('.ymq-options-box, .ymq-app-select-option, .ymq-app-select')) {
        setTimeout(updateFeeNotices, 200);
      }
    });
    document.addEventListener('change', e => {
      if (e.target.closest('.ymq-options-box')) {
        setTimeout(updateFeeNotices, 200);
      }
    });

    // Re-check when DOM mutates (for conditional sections appearing/disappearing)
    new MutationObserver(() => setTimeout(updateFeeNotices, 100)).observe(document.body, {
      childList: true,
      subtree: true,
    });

    updateFeeNotices();
  }

  function initCartPage() {
    lockFeeRows();
    syncFees();
    showItemCount();
    setInterval(pollCart, 2000);
    setInterval(updateCheckoutBlocking, 500);

    // Re-lock on DOM changes (cart re-renders)
    new MutationObserver(mutations => {
      let needsLock = false, needsCount = false;
      mutations.forEach(m => {
        m.addedNodes.forEach(n => {
          if (n.nodeType !== 1) return;
          if (n.textContent?.includes('Setup Fee') || n.classList?.contains('cart-item')) needsLock = true;
          if (n.textContent?.includes('Estimated total') || !document.getElementById('cart-item-count')) needsCount = true;
        });
      });
      if (needsLock) lockFeeRows();
      if (needsCount) showItemCount();
      updateCheckoutBlocking();
    }).observe(document.body, { childList: true, subtree: true });

    // Protect fee items in cart drawer too
    new MutationObserver(() => {
      const drawer = document.querySelector('cart-drawer, #CartDrawer');
      if (drawer) lockFeeRows();
      updateCheckoutBlocking();
    }).observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['class', 'style'],
    });
  }

  function init() {
    injectProtectionCSS();
    interceptFetch();
    hideSetupFeeProducts();

    const path = window.location.pathname;
    if (path.includes('/products/')) initProductPage();
    if (path.includes('/cart')) initCartPage();

    // Always keep checkout blocking in sync (covers drawer on any page)
    setInterval(updateCheckoutBlocking, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run collection hiding on delay for JS-rendered grids
  setTimeout(hideSetupFeeProducts, 300);
  setTimeout(hideSetupFeeProducts, 1000);

})();